// src/pages/settings/companyForm.jsx
// Shared field groups + form mapping used by BOTH Company.jsx (tab)
// and SetupWizard.jsx (first-run). Keeps the two perfectly in sync.
import React from 'react';

export const ACCENT = '#2e9f86';

export const inp = {
  width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 8,
  padding: '8px 11px', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  background: '#fafafa', color: '#111',
};

export const Field = ({ label, children, span2 }) => (
  <div style={{ gridColumn: span2 ? '1/-1' : undefined }}>
    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</label>
    {children}
  </div>
);

export function Toggle({ label, desc, example, checked, onChange }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '11px 13px', border: `1.5px solid ${checked ? '#a7f3d0' : '#e5e7eb'}`, borderRadius: 10, cursor: 'pointer', background: checked ? '#f0fdf8' : '#fafafa', transition: 'all .15s' }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{label}</div>
        {desc && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, lineHeight: 1.5 }}>{desc}</div>}
        {example && <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 3, fontStyle: 'italic' }}>{example}</div>}
      </div>
      <div style={{ width: 42, height: 24, borderRadius: 20, flexShrink: 0, background: checked ? ACCENT : '#d1d5db', position: 'relative', transition: 'background .2s', marginTop: 2 }}>
        <div style={{ position: 'absolute', top: 2, left: checked ? 20 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
      </div>
    </div>
  );
}

// settings doc -> flat form
export const fromSettings = (s) => ({
  name:            s?.company?.name            || '',
  name_ar:         s?.company?.name_ar         || '',
  address:         s?.company?.address         || '',
  phone:           s?.company?.phone           || '',
  email:           s?.company?.email           || '',
  tax_number:      s?.company?.tax_number      || '',
  receipt_footer:  s?.company?.receipt_footer  || 'Thank you!',
  logo_url:        s?.company?.logo_url        || '',

  base:             s?.currency?.base             || 'USD',
  show_lbp:         s?.currency?.show_lbp         ?? true,
  secondary_code:   s?.currency?.secondary_code   || 'LBP',
  secondary_symbol: s?.currency?.secondary_symbol || 'ل.ل',
  exchange_rate:    s?.exchange_rate              || 89500,

  variants:          s?.features?.variants          ?? true,
  locations:         s?.features?.locations         ?? true,
  tax:               s?.features?.tax               ?? false,
  default_tax_rate:  s?.features?.default_tax_rate  || 0,
  price_levels:      s?.features?.price_levels      ?? false,
  track_stock:       s?.features?.track_stock       ?? true,
  credit_sales:      s?.features?.credit_sales      ?? true,
  track_serials:     s?.features?.track_serials     ?? false,
  delivery:          s?.features?.delivery          ?? false,

  scale_enabled: s?.scale?.enabled ?? false,
  scale_prefix:  s?.scale?.prefix  || '20',
  scale_mode:    s?.scale?.mode    || 'weight',

  payment_method_id:   s?.defaults?.payment_method_id   || '',
  cash_customer_name:  s?.defaults?.cash_customer_name  || 'Walk-in',
  receipt_size:        s?.defaults?.receipt_size        || '80mm',
});

// flat form -> PUT payload
export const toPayload = (f) => ({
  exchange_rate: Number(f.exchange_rate) || 89500,
  company: {
    name: f.name, name_ar: f.name_ar, address: f.address, phone: f.phone,
    email: f.email, tax_number: f.tax_number, receipt_footer: f.receipt_footer,
    logo_url: f.logo_url || '',
  },
  currency: {
    base:             f.base,
    show_lbp:         !!f.show_lbp,
    secondary_code:   f.secondary_code   || 'LBP',
    secondary_symbol: f.secondary_symbol || 'ل.ل',
  },
  features: {
    variants:         !!f.variants,
    locations:        !!f.locations,
    tax:              !!f.tax,
    default_tax_rate: Number(f.default_tax_rate) || 0,
    price_levels:     !!f.price_levels,
    track_stock:      !!f.track_stock,
    credit_sales:     !!f.credit_sales,
    track_serials:    !!f.track_serials,
    delivery:         !!f.delivery,
  },
  scale: {
    enabled: !!f.scale_enabled,
    prefix:  String(f.scale_prefix || '20').replace(/\D/g, '').slice(0, 2) || '20',
    mode:    f.scale_mode === 'price' ? 'price' : 'weight',
  },
  defaults: {
    payment_method_id:  f.payment_method_id  || null,
    cash_customer_name: f.cash_customer_name || 'Walk-in',
    receipt_size:       f.receipt_size       || '80mm',
  },
});

/* ── field groups ── */

export function BusinessFields({ f, set }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <Field label="Company Name (EN) *">
        <input value={f.name} onChange={e => set('name', e.target.value)} style={inp} placeholder="My Shop" />
      </Field>
      <Field label="Company Name (AR)">
        <input value={f.name_ar} dir="rtl" onChange={e => set('name_ar', e.target.value)} style={inp} />
      </Field>
      <Field label="Phone">
        <input value={f.phone} onChange={e => set('phone', e.target.value)} style={inp} />
      </Field>
      <Field label="Email">
        <input value={f.email} onChange={e => set('email', e.target.value)} style={inp} />
      </Field>
      <Field label="Address" span2>
        <input value={f.address} onChange={e => set('address', e.target.value)} style={inp} />
      </Field>
      <Field label="Tax / VAT Number">
        <input value={f.tax_number} onChange={e => set('tax_number', e.target.value)} style={inp} />
      </Field>
      <Field label="Receipt Footer">
        <input value={f.receipt_footer} onChange={e => set('receipt_footer', e.target.value)} style={inp} />
      </Field>
    </div>
  );
}

export function CurrencyFields({ f, set }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <Field label="Base Currency">
        <select value={f.base} onChange={e => set('base', e.target.value)} style={inp}>
          <option value="USD">USD</option>
          <option value="LBP">LBP</option>
        </select>
      </Field>
      <Field label="Exchange Rate (secondary per 1 USD)">
        <input type="number" min="0" step="any" value={f.exchange_rate}
          onChange={e => set('exchange_rate', e.target.value)} style={inp} />
      </Field>
      <div style={{ gridColumn: '1/-1' }}>
        <Toggle
          label="💱 Second currency"
          desc="Show prices in two currencies side by side — on the POS, receipts, products, and reports."
          example="e.g. a $5 item also shows 447,500 LBP. Turn off if you only sell in one currency."
          checked={f.show_lbp}
          onChange={v => set('show_lbp', v)}
        />
      </div>
      {f.show_lbp && (
        <>
          <Field label="Secondary Currency Code">
            <input value={f.secondary_code} onChange={e => set('secondary_code', e.target.value)}
              style={inp} placeholder="LBP" maxLength={8} />
          </Field>
          <Field label="Secondary Currency Symbol">
            <input value={f.secondary_symbol} onChange={e => set('secondary_symbol', e.target.value)}
              style={inp} placeholder="ل.ل" maxLength={8} />
          </Field>
          <div style={{ gridColumn: '1/-1', fontSize: 11, color: '#9ca3af' }}>
            Preview: 1 USD = {Number(f.exchange_rate).toLocaleString()} {f.secondary_code}
          </div>
        </>
      )}
    </div>
  );
}

export function FeatureFields({ f, set }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <Toggle
        label="🎨 Product variants"
        desc="One product in several sizes, colors, or models — each with its own price, barcode, and stock."
        example="e.g. T-shirt → Small / Medium / Large · iPhone → 128GB / 256GB"
        checked={f.variants} onChange={v => set('variants', v)}
      />
      <Toggle
        label="⚖️ Weighing scale barcodes"
        desc="Sell items by weight using barcode labels printed by a weighing scale. The POS reads the weight from the barcode automatically."
        example="e.g. cheese, meat, vegetables sold per kg"
        checked={f.scale_enabled} onChange={v => set('scale_enabled', v)}
      />
      <Toggle
        label="💳 Credit sales"
        desc="Sell on account — the customer pays later and the debt is tracked on their account ledger."
        example="e.g. regular customers who settle at the end of the month"
        checked={f.credit_sales} onChange={v => set('credit_sales', v)}
      />
      <Toggle
        label="🚚 Delivery module"
        desc="Take delivery orders from the POS, assign drivers, track each order until the cash comes back, and settle drivers."
        example="e.g. restaurant or mini-market with delivery motorbikes"
        checked={f.delivery} onChange={v => set('delivery', v)}
      />
      <Toggle
        label="📊 Price levels"
        desc="Different price lists for different customer types. Assign a level to a customer and the POS charges it automatically."
        example="e.g. Retail / Wholesale / VIP prices"
        checked={f.price_levels} onChange={v => set('price_levels', v)}
      />
      <Toggle
        label="🔢 Serial / IMEI tracking"
        desc="Record a serial number on each sold item, so you know exactly which unit went to which customer."
        example="e.g. phones, laptops — useful for warranty claims"
        checked={f.track_serials} onChange={v => set('track_serials', v)}
      />
      <Toggle
        label="📦 Track stock"
        desc="Keep inventory counts — stock goes down when you sell, up when you purchase, with full movement history."
        example="Turn off only if you never count inventory"
        checked={f.track_stock} onChange={v => set('track_stock', v)}
      />
      <Toggle
        label="🏬 Multiple branches"
        desc="Run more than one location and tag documents and users per branch."
        example="e.g. main store + warehouse"
        checked={f.locations} onChange={v => set('locations', v)}
      />
      <Toggle
        label="🧾 Tax"
        desc="Add tax on documents with a default rate."
        example="Turn on only if you charge VAT/tax on sales"
        checked={f.tax} onChange={v => set('tax', v)}
      />
      {f.tax && (
        <Field label="Default Tax Rate (%)">
          <input type="number" min="0" step="any" value={f.default_tax_rate}
            onChange={e => set('default_tax_rate', e.target.value)} style={inp} />
        </Field>
      )}
      {f.scale_enabled && (
        <div style={{ gridColumn: '1/-1', border: '1.5px solid #fcd34d', background: '#fffbeb', borderRadius: 10, padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>⚖️ Scale barcode setup — must match your scale's configuration</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Barcode prefix (2 digits)">
              <input value={f.scale_prefix}
                onChange={e => set('scale_prefix', e.target.value.replace(/\D/g, '').slice(0, 2))}
                style={inp} placeholder="20" />
            </Field>
            <Field label="Embedded value">
              <select value={f.scale_mode} onChange={e => set('scale_mode', e.target.value)} style={inp}>
                <option value="weight">Weight (grams)</option>
                <option value="price">Price (2 decimals)</option>
              </select>
            </Field>
          </div>
          <div style={{ fontSize: 11, color: '#92400e', marginTop: 8, lineHeight: 1.6 }}>
            Label layout: <b style={{ fontFamily: 'monospace' }}>{f.scale_prefix || '20'} + 5-digit product code (PLU) + 5-digit {f.scale_mode === 'price' ? 'price' : 'weight'} + check digit</b>.
            When creating a scale product, choose type <b>Scaled</b> and enter only its 5-digit PLU in the barcode field.
          </div>
        </div>
      )}
      <div style={{ gridColumn: '1/-1', fontSize: 11.5, color: '#065f46', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 10, padding: '10px 13px', lineHeight: 1.6 }}>
        💡 <b>Tip — services:</b> to sell repairs, labor, or fees, no feature is needed here. Just create a product and turn off "Track Stock" in the product form — it sells normally but never affects inventory.
      </div>
    </div>
  );
}

export function DefaultFields({ f, set, paymentMethods = [] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <Field label="Default Payment Method">
        <select value={f.payment_method_id} onChange={e => set('payment_method_id', e.target.value)} style={inp}>
          <option value="">— None —</option>
          {paymentMethods.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
        </select>
      </Field>
      <Field label="Walk-in Customer Name">
        <input value={f.cash_customer_name} onChange={e => set('cash_customer_name', e.target.value)} style={inp} />
      </Field>
      <Field label="Receipt Size">
        <select value={f.receipt_size} onChange={e => set('receipt_size', e.target.value)} style={inp}>
          <option value="80mm">80mm thermal</option>
          <option value="a4">A4</option>
        </select>
      </Field>
    </div>
  );
}